import * as PIXI from 'pixi.js';

export class InputField extends PIXI.Text {
  public inputElement?: HTMLInputElement;
  private backgroundDisplay?: PIXI.Sprite | PIXI.Graphics | PIXI.Text;
  private isEditing = false;
  private preEditIndex = 0;
  public editCursor: string = '|';
  private isCursorVisible = true;
  private cursorTimer?: number;

  private readonly onInput = (e: Event) => this._UpdateText(e);
  private readonly onKeydown = (e: KeyboardEvent) => this._UpdateText(e);
  private readonly onFocusout = (e: FocusEvent) => this._EndEdit(e);
  private readonly onFocus = () => this._StartCursorBlink();
  private readonly onBlur = () => this._StopCursorBlink();

  constructor(
    options?: Partial<{
      text: string;
      style: PIXI.TextStyle;
      backgroundDisplay: PIXI.Sprite | PIXI.Graphics | PIXI.Text;
      inputElement: HTMLInputElement;
    }>
  ) {
    super(options?.text || '', options?.style);

    if (options?.inputElement) {
      this.inputElement = options.inputElement;
    } else {
      const _inputElement = document.createElement('input');
      _inputElement.style.setProperty('height', '0px');
      _inputElement.style.setProperty('width', '0px');
      _inputElement.style.setProperty('padding', '0px');
      _inputElement.style.setProperty('margin', '0px');
      _inputElement.style.setProperty('position', 'fixed');
      _inputElement.style.setProperty('opacity', '0');
      document.body.appendChild(_inputElement);
      this.inputElement = _inputElement;
    }

    this.backgroundDisplay = options?.backgroundDisplay ?? this;
    this.backgroundDisplay.interactive = true;
    this.backgroundDisplay.on('pointerdown', this._ClickFocus.bind(this));
    this.BindInputElementEvent(this.inputElement);
  }

  get text(): string {
    return super.text;
  }

  set text(value: string) {
    super.text = value;
  }

  private _ClickFocus(e: any) {
    e.preventDefault();
    this.inputElement?.focus();
    this.isEditing = true;
    this._UpdateText(e);
  }

  private _EndEdit(e: any) {
    e.preventDefault?.();
    this.isEditing = false;
    this.isCursorVisible = false;
    this._StopCursorBlink();
    this._UpdateText(e);
  }

  private _StartCursorBlink() {
    this.isCursorVisible = true;
    this._StopCursorBlink();
    this.cursorTimer = window.setInterval(() => {
      this.isCursorVisible = !this.isCursorVisible;
      this._UpdateText({ key: undefined } as any);
    }, 530);
  }

  private _StopCursorBlink() {
    if (this.cursorTimer) {
      window.clearInterval(this.cursorTimer);
      this.cursorTimer = undefined;
    }
  }

  private _UpdateText(e: any) {
    let _offset = 0;
    if (e?.key === 'ArrowLeft') _offset = -1;
    if (e?.key === 'ArrowRight') _offset = 1;

    let _editIdx = this.preEditIndex;
    if (this.inputElement && this.inputElement.selectionStart !== null) {
      _editIdx = this.inputElement.selectionStart + _offset;
      this.preEditIndex = Math.max(0, _editIdx);
    }

    const _text = (this.inputElement?.value as string) || '';

    if (this.isEditing) {
      const cursor = this.isCursorVisible ? this.editCursor : '';
      this.text = _text.slice(0, this.preEditIndex) + cursor + _text.slice(this.preEditIndex);
    } else {
      this.text = _text;
    }
  }

  public BindInputElementEvent(inputElement: HTMLInputElement) {
    if (this.inputElement) {
      this.UnBindInputElementEvent();
    }
    this.inputElement = inputElement;
    this.inputElement?.addEventListener('input', this.onInput);
    this.inputElement?.addEventListener('keydown', this.onKeydown);
    this.inputElement?.addEventListener('focusout', this.onFocusout);
    this.inputElement?.addEventListener('focus', this.onFocus);
    this.inputElement?.addEventListener('blur', this.onBlur);
  }

  public UnBindInputElementEvent() {
    this.inputElement?.removeEventListener('input', this.onInput);
    this.inputElement?.removeEventListener('keydown', this.onKeydown);
    this.inputElement?.removeEventListener('focusout', this.onFocusout);
    this.inputElement?.removeEventListener('focus', this.onFocus);
    this.inputElement?.removeEventListener('blur', this.onBlur);
    this._StopCursorBlink();
    this.inputElement = undefined;
  }

  public GetInputText() {
    return (this.inputElement?.value as string) || '';
  }
}
